import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "../i18n";
import { stripLocaleFromPath, buildLocalePath } from "../lib/locale-utils";

const BASE = "https://wccalendar.com";

/**
 * Injects <link rel="alternate" hreflang="..."> tags into <head>.
 * Uses locale-prefixed URL paths (e.g. /es/faq).
 */
export default function HreflangTags() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Remove any existing hreflang links
    document.querySelectorAll('link[data-hreflang]').forEach((el) => el.remove());

    const basePath = stripLocaleFromPath(window.location.pathname);

    // Add x-default (English, no prefix)
    const defaultLink = document.createElement("link");
    defaultLink.rel = "alternate";
    defaultLink.hreflang = "x-default";
    defaultLink.href = `${BASE}${basePath}`;
    defaultLink.setAttribute("data-hreflang", "true");
    document.head.appendChild(defaultLink);

    // Add each supported language
    for (const lang of SUPPORTED_LANGUAGES) {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = lang;
      link.href = `${BASE}${buildLocalePath(lang as SupportedLanguage, basePath)}`;
      link.setAttribute("data-hreflang", "true");
      document.head.appendChild(link);
    }

    // Update meta description for current language
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && i18n.isInitialized) {
      metaDesc.setAttribute("content", i18n.t("meta.description"));
    }

    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", i18n.t("meta.og_title"));
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", i18n.t("meta.og_description"));
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute("content", i18n.t("meta.twitter_description"));
  }, [i18n.language]);

  return null;
}
